import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Alert, Card, Button, ProgressBar, Stack, Form, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { createCase } from '../services/api';
import { SpecialtySelect } from '../components/SpecialtySelect';
import { enumLabel } from '../labels';
import { getLang } from '../language';

// Types come from the OpenAPI contract (the single source of truth). The form fields are
// hand-built, but they are typed against CreateCaseRequest — if the contract changes,
// TypeScript flags this form.
type CreateCaseRequest = components['schemas']['CreateCaseRequest'];
type Priority = components['schemas']['Priority'];
type Sex = components['schemas']['Sex'];
type FundingStatus = components['schemas']['FundingStatus'];

const PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
const SEXES: Sex[] = ['MALE', 'FEMALE', 'OTHER', 'UNKNOWN'];
const FUNDING: FundingStatus[] = ['SELF_FUNDED', 'CHARITY_FUNDED', 'PENDING'];

const STEP_TITLES = ['Patient', 'Clinical', 'Case', 'Consent'];

/** Shape mirrors CreateCaseRequest, flattened for the form (patient/consent are nested on submit). */
interface FormValues {
  // Patient
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: Sex;
  country: string;
  contactNumber?: string;
  preferredLanguage: string;
  // Clinical
  clinicalContext: string;
  description?: string;
  problemDuration?: string;
  medicalConditions?: string;
  medicines?: string;
  allergies?: string;
  // Case
  title: string;
  priority: Priority;
  specialtyId?: string;
  fundingStatus?: FundingStatus;
  // Consent
  consentTextVersion: string;
}

export function CreateCase({
  onCreated,
  defaultSourceLang = 'ar',
}: {
  onCreated: (caseId: string) => void;
  defaultSourceLang?: string;
}) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      sex: 'UNKNOWN',
      priority: 'MEDIUM',
      preferredLanguage: defaultSourceLang,
    },
    mode: 'onTouched',
  });

  // Register specialtyId so the searchable SpecialtySelect can drive it via setValue.
  useEffect(() => {
    register('specialtyId');
  }, [register]);
  const specialtyId = watch('specialtyId');

  // Fields validated per step before advancing (so the wizard blocks on the current step only).
  const stepFields: Array<Array<keyof FormValues>> = [
    ['firstName', 'lastName', 'dateOfBirth', 'sex', 'country', 'preferredLanguage'],
    ['clinicalContext'],
    ['title', 'priority'],
    ['consentTextVersion'],
  ];

  const next = async () => {
    if (await trigger(stepFields[step])) setStep((s) => Math.min(STEP_TITLES.length - 1, s + 1));
  };

  const onSubmit = async (v: FormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const body: CreateCaseRequest = {
        title: v.title,
        priority: v.priority,
        specialtyId: v.specialtyId || null,
        fundingStatus: v.fundingStatus,
        sourceLang: defaultSourceLang,
        clinicalContext: v.clinicalContext,
        description: v.description || null,
        problemDuration: v.problemDuration || null,
        medicalConditions: v.medicalConditions || null,
        medicines: v.medicines || null,
        allergies: v.allergies || null,
        patient: {
          firstName: v.firstName,
          lastName: v.lastName,
          dateOfBirth: v.dateOfBirth,
          sex: v.sex,
          country: v.country,
          contactNumber: v.contactNumber || null,
          preferredLanguage: v.preferredLanguage,
        },
        consent: { consentTextVersion: v.consentTextVersion },
      };
      const created = await createCase(body);
      onCreated(created.caseId);
    } catch (e) {
      const err = e as { error?: { message?: string } };
      setError(err?.error?.message ?? 'Failed to create case');
    } finally {
      setSubmitting(false);
    }
  };

  const isLast = step === STEP_TITLES.length - 1;

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Card.Title as="h2" className="h4 mb-1">Create a case</Card.Title>
        <Card.Subtitle className="text-muted mb-3">
          Step {step + 1} of {STEP_TITLES.length}: {STEP_TITLES[step]}
        </Card.Subtitle>
        <ProgressBar now={((step + 1) / STEP_TITLES.length) * 100} className="mb-3" style={{ height: 6 }} />

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Step 0 — Patient */}
          <div hidden={step !== 0}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First name</Form.Label>
                  <Form.Control isInvalid={!!errors.firstName} {...register('firstName', { required: 'Required' })} />
                  <Form.Control.Feedback type="invalid">{errors.firstName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last name</Form.Label>
                  <Form.Control isInvalid={!!errors.lastName} {...register('lastName', { required: 'Required' })} />
                  <Form.Control.Feedback type="invalid">{errors.lastName?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date of birth</Form.Label>
                  <Form.Control type="date" isInvalid={!!errors.dateOfBirth} {...register('dateOfBirth', { required: 'Required' })} />
                  <Form.Control.Feedback type="invalid">{errors.dateOfBirth?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sex</Form.Label>
                  <Form.Select {...register('sex', { required: true })}>
                    {SEXES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control isInvalid={!!errors.country} {...register('country', { required: 'Required' })} />
                  <Form.Control.Feedback type="invalid">{errors.country?.message}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact number</Form.Label>
                  <Form.Control {...register('contactNumber')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Preferred language</Form.Label>
                  <Form.Control {...register('preferredLanguage', { required: 'Required' })} />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Step 1 — Clinical */}
          <div hidden={step !== 1}>
            <Form.Group className="mb-3">
              <Form.Label>Clinical context</Form.Label>
              <Form.Control as="textarea" rows={4} isInvalid={!!errors.clinicalContext}
                {...register('clinicalContext', { required: 'Required' })} />
              <Form.Control.Feedback type="invalid">{errors.clinicalContext?.message}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} {...register('description')} />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Problem duration</Form.Label>
                  <Form.Control placeholder="e.g. 3 weeks" {...register('problemDuration')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Allergies</Form.Label>
                  <Form.Control {...register('allergies')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Medical conditions</Form.Label>
                  <Form.Control as="textarea" rows={2} {...register('medicalConditions')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Medicines</Form.Label>
                  <Form.Control as="textarea" rows={2} {...register('medicines')} />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Step 2 — Case */}
          <div hidden={step !== 2}>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control isInvalid={!!errors.title} {...register('title', { required: 'Required' })} />
              <Form.Control.Feedback type="invalid">{errors.title?.message}</Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select {...register('priority', { required: true })}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{enumLabel('priority', p, getLang())}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Specialty</Form.Label>
                  <SpecialtySelect
                    value={specialtyId}
                    onChange={(id) => setValue('specialtyId', id || undefined)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Funding status</Form.Label>
                  <Form.Select {...register('fundingStatus')}>
                    <option value="">—</option>
                    {FUNDING.map((f) => <option key={f} value={f}>{enumLabel('fundingStatus', f, getLang())}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Step 3 — Consent */}
          <div hidden={step !== 3}>
            <Form.Group className="mb-3">
              <Form.Label>Consent / privacy agreement version</Form.Label>
              <Form.Control isInvalid={!!errors.consentTextVersion}
                placeholder="e.g. v1.0"
                {...register('consentTextVersion', { required: 'Required' })} />
              <Form.Control.Feedback type="invalid">{errors.consentTextVersion?.message}</Form.Control.Feedback>
              <Form.Text muted>Recording consent is required to create the case.</Form.Text>
            </Form.Group>
          </div>

          <Stack direction="horizontal" gap={2} className="mt-3">
            <Button variant="outline-secondary" disabled={step === 0 || submitting}
              onClick={() => setStep((s) => Math.max(0, s - 1))} type="button">
              Back
            </Button>
            <div className="ms-auto" />
            {!isLast && (
              <Button variant="primary" onClick={next} type="button">Next</Button>
            )}
            {isLast && (
              <Button variant="success" type="submit" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create case'}
              </Button>
            )}
          </Stack>
        </Form>
      </Card.Body>
    </Card>
  );
}
